/**
 * A bundle import's storing (decision 7, L10, L11; part 24c log, principles 1, 2 and 6, table S). A bundle can be
 * gigabytes and the page holds one batch at a time, so its records are created batch by batch under the book's ID behind a
 * pending-import marker, and the book appears only in the write that creates its record and deletes the marker. Every
 * write is a create, so abandoning the import deletes only what it created; every write goes through the edit session's
 * write bound to the turn the import began in, so nothing of it lands after the tab lost the lock. Each operation runs in
 * the snapshot history's queue, as every book operation does (part 08, principle 6).
 *
 * Part 24d (L11's update, principles 1, 2): an update stages the bundle under a hidden staging book the same way, then
 * hands it to the existing book in one write with the texts, copies and taken offers ({@link BundleImport.update}).
 */
import type { SessionWriteResult } from './edit-lock.ts';
import type { SnapshotHistory } from './history.ts';
import { KeyTakenError, StoreRecordError } from './records.ts';
import type { BookRecord, ChapterEntry, NoteRecord, ShareRecord } from './records.ts';
import type { ImportOp, Store, StoreFailure, StoreOp } from './store.types.ts';

/** What a {@link BundleImport} needs; everything with an effect is injected. */
export interface BundleImportOptions {
  readonly store: Store;
  /** A fresh ID: an update's conflict copies (part 24d). */
  readonly newId: () => string;
  /** The edit session's write bound to the turn the import begins in (`EditSession.writer`, principle 6). */
  readonly write: (ops: readonly StoreOp[]) => Promise<SessionWriteResult>;
  /** This device's snapshot history: the import's writes run in its queue. */
  readonly history: SnapshotHistory;
  /** Milliseconds since the Unix epoch. */
  readonly now: () => number;
}

/**
 * The outcome of one of the import's writes: done; a key the store already holds (`taken`, principle 2: nothing of the
 * write applied); the lock gone (`stale`); or the store failed.
 */
export type ImportWriteResult = { readonly kind: 'done' } | { readonly kind: 'taken' } | { readonly kind: 'stale' } | StoreFailure;

/** The outcome of {@link BundleImport.begin}: the import under way, or something of the book already stored (L3, table HB). */
export type BeginImportResult = { readonly kind: 'begun'; readonly import: BundleImport } | { readonly kind: 'exists' } | Exclude<ImportWriteResult, { readonly kind: 'done' }>;

/**
 * Part 24d, table U: a chapter an update changes: its stored text as the review read it (`before`; '' for none, U9) and as
 * the update makes it, and whether `before` is kept as a conflict copy (U5, U6).
 */
export interface UpdatedText {
  readonly id: string;
  readonly before: string;
  readonly after: string;
  readonly copy: boolean;
}

/** Part 24d, U11, F2–F5: a record as the review read it, and what the update writes over it. */
export interface Reviewed<T> {
  readonly before: T;
  readonly after: T;
}

/**
 * Part 24d, S′5: what an update writes into book `bookId` with the staged records: the matched chapters' staging IDs to
 * their own (N1), the changed texts, each chapter entry the review read (with a taken offer's fields in `after`), the
 * notes and book record whose offers were taken, each share both hold merged (part 38b, table BU), and the recovered
 * chapters' records (N12, 24c's P23: created under the staging book, so they are adopted with the rest). Every entry,
 * note, share and book record named is checked against what the review read (U11) and written as `after`.
 */
export interface UpdateRequest {
  readonly bookId: string;
  readonly chapters: readonly (readonly [string, string])[];
  readonly texts: readonly UpdatedText[];
  readonly entries: readonly Reviewed<ChapterEntry>[];
  readonly notes: readonly Reviewed<NoteRecord>[];
  /** Part 38b, BU: each share the book and the bundle both hold, as read and as merged. */
  readonly shares: readonly Reviewed<ShareRecord>[];
  readonly book: Reviewed<BookRecord> | null;
  /** G′2 (deep-fix r2): every chapter both have whose entry the review read in the book: each must still be. */
  readonly present: readonly string[];
  readonly ops: readonly ImportOp[];
}

/**
 * The outcome of {@link BundleImport.update}: written; `changed` when something the review read isn't so any more
 * (U11), nothing written; or a write's failure.
 */
export type UpdateResult = { readonly kind: 'updated' } | { readonly kind: 'changed' } | Exclude<ImportWriteResult, { readonly kind: 'done' }>;

/** A write's result as the import says it: a failure that is a taken key is `taken` (principle 2). */
function outcome(result: SessionWriteResult): ImportWriteResult {
  if (result.kind === 'committed') return { kind: 'done' };
  if (result.kind === 'failed' && result.error instanceof KeyTakenError) return { kind: 'taken' };
  return result;
}

/** One bundle import of book `bookId`, from its marker to its book or its abandoning (table S). */
export class BundleImport {
  readonly bookId: string;
  private readonly options: BundleImportOptions;

  private constructor(options: BundleImportOptions, bookId: string) {
    this.options = options;
    this.bookId = bookId;
  }

  /**
   * S1, L3: in one turn of the history's queue, `exists` when anything of book `bookId` is stored (table HB, a pending
   * import of it included); else its marker created, and the import under way.
   */
  static begin(options: BundleImportOptions, bookId: string): Promise<BeginImportResult> {
    return options.history.exclusive(async (): Promise<BeginImportResult> => {
      if (await options.store.hasBook(bookId)) return { kind: 'exists' };
      const begun = outcome(await options.write([{ kind: 'add', store: 'imports', value: { id: bookId, startedAt: options.now() } }]));
      return begun.kind === 'done' ? { kind: 'begun', import: new BundleImport(options, bookId) } : begun;
    });
  }

  /** S2: creates `ops` (one batch's records) in one write. */
  stage(ops: readonly ImportOp[]): Promise<ImportWriteResult> {
    return this.options.history.exclusive(async () => outcome(await this.options.write(ops)));
  }

  /**
   * S3, S12: in one write, creates `ops` (the last records) and `book`, and deletes the marker: the book appears whole.
   * A book record already there is `taken`, and nothing is written.
   */
  commit(book: BookRecord, ops: readonly ImportOp[]): Promise<ImportWriteResult> {
    return this.options.history.exclusive(async () =>
      outcome(await this.options.write([...ops, { kind: 'add', store: 'books', value: book }, { kind: 'delete', store: 'imports', id: this.bookId }])),
    );
  }

  /**
   * Part 24d, S′5, S′6, U11: the staged import handed to book `request.bookId`. In the history's queue, `changed` unless
   * every entry, note, share (part 38b) and book record the review read is still as read and every changed chapter is in the book with its
   * stored text the one reviewed; then a `before-update` snapshot of each changed text (one not taken: nothing written);
   * then, checked again in the queue, one write: the recovered chapters and every staged record adopted, each text, a
   * `bundle-update` conflict copy of each `before` asked for (a fresh ID), each taken offer and each merged share.
   */
  async update(request: UpdateRequest): Promise<UpdateResult> {
    const { history, store, now, newId } = this.options;
    const { bookId } = request;
    const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
    const current = async (): Promise<boolean> => {
      for (const { before } of request.entries) if (!same(await store.read('entries', before.id), before)) return false;
      for (const { before } of request.notes) if (!same(await store.read('notes', before.id), before)) return false;
      for (const { before } of request.shares) if (!same(await store.read('shares', before.id), before)) return false;
      if (request.book !== null && !same(await store.read('books', bookId), request.book.before)) return false;
      // G′2 (deep-fix r2): every chapter the review read, and every chapter whose text changes, still in the book; a chapter
      // deleted forever since is reviewed again (G′3).
      for (const id of new Set([...request.present, ...request.texts.map((text) => text.id)])) if ((await store.read('entries', id))?.bookId !== bookId) return false;
      // U9: a chapter with no text record reads as empty.
      for (const text of request.texts) if (((await store.read('chapters', text.id))?.text ?? '') !== text.before) return false;
      return true;
    };
    // A record its reader now refuses is as much a change as one rewritten; a read that fails (a store closed under the
    // update, deep-fix r1) is the store's failure, which ends the update, never a change to review again.
    const checked = (): Promise<{ readonly kind: 'same' } | { readonly kind: 'changed' } | StoreFailure> =>
      current().then(
        (same) => (same ? { kind: 'same' } : { kind: 'changed' }),
        (error: unknown) => (error instanceof StoreRecordError ? { kind: 'changed' } : { kind: 'failed', error }),
      );
    const first = await history.exclusive(checked);
    if (first.kind !== 'same') return first;
    for (const text of request.texts) {
      const taken = await history.take({ id: text.id, bookId, text: text.before }, 'before-update');
      if (taken.kind === 'stale' || taken.kind === 'failed') return taken;
    }
    return history.exclusive(async (): Promise<UpdateResult> => {
      const again = await checked();
      if (again.kind !== 'same') return again;
      const at = now();
      const ops: StoreOp[] = [
        // The recovered chapters' records, made under the staging book, go over with the rest.
        ...request.ops,
        { kind: 'adopt-import', from: this.bookId, to: bookId, chapters: request.chapters },
        ...request.texts.map((text): StoreOp => ({ kind: 'put', store: 'chapters', value: { id: text.id, bookId, text: text.after } })),
        ...request.texts
          .filter((text) => text.copy)
          .map((text): StoreOp => ({ kind: 'add', store: 'conflictCopies', value: { id: newId(), chapterId: text.id, bookId, text: text.before, createdAt: at, origin: 'bundle-update' } })),
        ...request.entries.map(({ after }): StoreOp => ({ kind: 'put', store: 'entries', value: after })),
        ...request.notes.map(({ after }): StoreOp => ({ kind: 'put', store: 'notes', value: after })),
        ...request.shares.map(({ after }): StoreOp => ({ kind: 'put', store: 'shares', value: after })),
        ...(request.book === null ? [] : [{ kind: 'put', store: 'books', value: request.book.after } satisfies StoreOp]),
      ];
      const written = outcome(await this.options.write(ops));
      return written.kind === 'done' ? { kind: 'updated' } : written;
    });
  }

  /** S4–S7, S9: deletes everything the import created and its marker, in one write. */
  abandon(): Promise<ImportWriteResult> {
    return this.options.history.exclusive(async () => outcome(await this.options.write([{ kind: 'abandon-import', bookId: this.bookId }])));
  }
}

/**
 * S8: abandons every pending import in the store, each in its own write (a marker its reader refuses by its key, when that
 * is text): one that crashed, was closed, or belongs to an earlier turn of this tab or another's. The writes' outcomes in
 * the markers' order; a marker whose abandon wasn't written stays, and the next turn tries again (S9).
 */
export async function abandonPending(store: Store, write: (ops: readonly StoreOp[]) => Promise<SessionWriteResult>): Promise<ImportWriteResult[]> {
  const { records, invalid } = await store.readAll('imports');
  const keys = [...records.map((record) => record.id), ...invalid.map((record) => record.key).filter((key) => typeof key === 'string')];
  const results: ImportWriteResult[] = [];
  for (const bookId of keys) results.push(outcome(await write([{ kind: 'abandon-import', bookId }])));
  return results;
}

/** Table S: the keys of the books a pending import is creating, readable or not, which the book list never shows. */
export async function pendingBooks(store: Store): Promise<ReadonlySet<unknown>> {
  const { records, invalid } = await store.readAll('imports');
  return new Set<unknown>([...records.map((record) => record.id), ...invalid.map((record) => record.key)]);
}
